<template>
    <div>
        <HamletTable
            ref="hamletTable"
            :columns="fields"
            :fetch="fetch"
            :search="true"
            table-container-class="table-responsive hamlet-table discovery-requests-table"
            :defaultPerPage="savedPerPage"
            :default-filters="filters"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
        >
            <template #header-label>
                Research Discovery
            </template>

            <template #cell-status="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ data.item.status }}
                </b-badge>
            </template>

            <template #cell-providerJobId="data">
                {{ data.item.providerJobId || '-' }}
            </template>

            <template #cell-productIdentifier="data">
                <a
                    v-if="data.item.productId"
                    :href="productCardUrl(data.item.productId)"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click.stop
                >
                    {{ data.item.productIdentifier || '-' }}
                </a>
                <span v-else>{{ data.item.productIdentifier || '-' }}</span>
            </template>

            <template #cell-productName="data">
                <a
                    v-if="data.item.productId"
                    :href="productCardUrl(data.item.productId)"
                    target="_blank"
                    rel="noopener noreferrer"
                    @click.stop
                >
                    {{ data.item.productName || '-' }}
                </a>
                <span v-else>{{ data.item.productName || '-' }}</span>
            </template>

            <template #cell-createdAt="data">
                {{ formatDate(data.item.createdAt) }}
            </template>

            <template #cell-updatedAt="data">
                {{ formatDate(data.item.updatedAt) }}
            </template>

            <template #filter-cell-status>
                <VueSelectFilter
                    style="min-width: 130px;"
                    :options="statusOptions"
                    :initial-value="selectedStatusOptions"
                    @filter="filterByStatus"
                />
            </template>

            <template #filter-cell-engineId>
                <VueSelectFilter
                    style="min-width: 120px;"
                    :options="engineOptions"
                    :initial-value="selectedEngineOptions"
                    @filter="filterByEngine"
                />
            </template>

            <template #filter-cell-productId>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.productId"
                    @search="filterByProductId"
                />
            </template>

            <template #filter-cell-productIdentifier>
                <FilterInput
                    style="min-width: 140px;"
                    :initial-value="filters.productIdentifier"
                    @search="filterByProductIdentifier"
                />
            </template>

            <template #filter-cell-productName>
                <FilterInput
                    style="min-width: 140px;"
                    :initial-value="filters.productName"
                    @search="filterByProductName"
                />
            </template>
        </HamletTable>
    </div>
</template>

<script>
import {BBadge} from 'bootstrap-vue'
import {HamletTable} from '@core/components/common'
import {FilterInput, VueSelectFilter} from '@core/components/common/filter'
import {ResearchDiscoveryApi} from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import dateRenderer from '@core/mixins/date-renderer'

const DEFAULT_PER_PAGE = 20

function normalizeSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
        }
    }

    return {
        filters: structuredClone(settings.filters || {}),
        perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
        page: Number(settings.page) || 1,
    }
}

export default {
    components: {
        BBadge,
        HamletTable,
        FilterInput,
        VueSelectFilter,
    },
    mixins: [backendResponse, dateRenderer],
    data() {
        const savedSettings = normalizeSettings(this.$store.getters.runtimeConfig.researchDiscoveryRequestsSettings)

        return {
            filters: savedSettings.filters,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,
            fields: [
                {key: 'status', label: 'Status'},
                {key: 'engineId', label: 'Engine'},
                {key: 'providerJobId', label: 'Provider Job ID'},
                {key: 'productIdentifier', label: 'Product Identifier'},
                {key: 'productName', label: 'Product Name'},
                {key: 'supplierName', label: 'Supplier'},
                {key: 'createdAt', label: 'Created At'},
                {key: 'updatedAt', label: 'Updated At'},
            ],
            statusOptions: [
                {status: 'created', title: 'created'},
                {status: 'dispatched', title: 'dispatched'},
                {status: 'processing', title: 'processing'},
                {status: 'completed', title: 'completed'},
                {status: 'failed', title: 'failed'},
            ],
            engineOptions: [
                {engineId: 'stub', title: 'stub'},
                {engineId: 'aignition', title: 'aignition'},
            ],
        }
    },
    computed: {
        selectedStatusOptions() {
            if (!this.filters.status) {
                return []
            }

            return this.statusOptions.filter(option => option.status === this.filters.status)
        },
        selectedEngineOptions() {
            if (!this.filters.engineId) {
                return []
            }

            return this.engineOptions.filter(option => option.engineId === this.filters.engineId)
        },
    },
    methods: {
        formatDate(value) {
            if (!value) {
                return '-'
            }

            return this.renderDateTime(value)
        },
        statusVariant(status) {
            const map = {
                created: 'secondary',
                dispatched: 'info',
                processing: 'warning',
                completed: 'success',
                failed: 'danger',
            }

            return map[status] || 'secondary'
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage
            this.saveSettings()
        },
        onPageChange(page) {
            this.savedPage = page
            this.saveSettings()
        },
        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'researchDiscoveryRequestsSettings',
                value: {
                    filters: this.filters,
                    perPage: this.savedPerPage,
                    page: this.savedPage,
                },
                removeOnOrganizationSwitch: true,
            })
        },
        setFilterValue(key, value) {
            if (value === null || value === undefined || value === '') {
                if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                    delete this.filters[key]
                }
                return
            }

            this.filters[key] = value
        },
        filterByStatus(items) {
            const status = items.length > 0 ? items[0].status : null
            this.setFilterValue('status', status)
            this.$refs.hamletTable.filter('status', status)
            this.saveSettings()
        },
        filterByEngine(items) {
            const engineId = items.length > 0 ? items[0].engineId : null
            this.setFilterValue('engineId', engineId)
            this.$refs.hamletTable.filter('engineId', engineId)
            this.saveSettings()
        },
        filterByProductId(value) {
            this.setFilterValue('productId', value)
            this.$refs.hamletTable.filter('productId', value)
            this.saveSettings()
        },
        filterByProductIdentifier(value) {
            this.setFilterValue('productIdentifier', value)
            this.$refs.hamletTable.filter('productIdentifier', value)
            this.saveSettings()
        },
        filterByProductName(value) {
            this.setFilterValue('productName', value)
            this.$refs.hamletTable.filter('productName', value)
            this.saveSettings()
        },
        onRowClick(item) {
            this.$router.push({name: 'research-discovery-card', params: {id: item.requestId}})
        },
        productCardUrl(productId) {
            const resolved = this.$router.resolve({
                name: 'product-card',
                params: {id: productId},
            })

            return resolved.href
        },
        fetch(config, cb) {
            this.savedPage = config.page
            this.savedPerPage = config.perPage
            this.saveSettings()

            return ResearchDiscoveryApi
                .getRequests(config.filters, config.page, config.perPage)
                .then((response) => {
                    cb(response.data.total, response.data.items)
                })
                .catch((e) => {
                    cb(0, [])
                    this.handleBackendError(e)
                })
        },
    },
}
</script>

<style>
.discovery-requests-table table th,
.discovery-requests-table table td {
    padding: 0.72rem 1rem;
}
</style>
